import React, { ReactNode, useEffect, useState } from 'react'

import Link from 'next/link'
import { MusicAppLayout } from '@audio-network/layouts'
import dynamic from 'next/dynamic'
import { getCurrentCart } from '../../../services/cartService'
import useFavouritesStore from '@/stores/favouritesStore'
import useProjectStore from '@/stores/projectStore'
import { useRouter } from 'next/router'
import { useSession } from '../../../context/Session'

const ServiceMenu = dynamic(
	() => import('@/components/shared/ServiceMenu/ServiceMenu'),
	{
		ssr: false, // This line ensures that the component is not rendered server side
	}
)

type Props = {
	children: ReactNode
	interfaceText: { [key: string]: string }
}

const LinkComponent = ({ children, href }): JSX.Element => {
	return (
		<Link legacyBehavior href={href} passHref>
			{children}
		</Link>
	)
}

export default function MusicAppLayoutWrapper({
	children,
	interfaceText,
}: Props): JSX.Element {
	const [cartCount, cartCountSet] = useState<number>(0)
	const { loadingSession, user } = useSession()
	const getProjects = useProjectStore((state) => state.getProjects)
	const projects = useProjectStore((state) => state.projects)
	const setFavouriteTracks = useFavouritesStore(
		(state) => state.setFavouriteTracks
	)
	const router = useRouter()
	const { events } = router

	if (!projects && user) {
		getProjects()
	}

	useEffect(() => {
		user?.favouriteTracks && setFavouriteTracks(user.favouriteTracks)
	}, [setFavouriteTracks, user])

	useEffect(() => {
		getCurrentCart().then((cart) => {
			if (cart) cartCountSet(cart.itemsCount)
		})
	}, [])

	useEffect(() => {
		if (typeof document === 'undefined') return
		const mainElement = document.querySelector('main')

		if (!mainElement) return

		const handleRouteChange = (path: string): void => {
			if (path.startsWith('/track/searchkeyword')) return
			mainElement.scrollTo(0, 0)
		}

		events.on('routeChangeComplete', handleRouteChange)
		return () => events.off('routeChangeComplete', handleRouteChange)
	}, [events])

	return (
		<>
			<MusicAppLayout
				interfaceText={interfaceText}
				user={user}
				isUserLoading={loadingSession}
				cartCount={cartCount}
				customLinkComponent={user && LinkComponent}
				projects={projects}
				newLayout
				newHeader
				newSidebar={false}
			>
				{children}
			</MusicAppLayout>
			{user && <ServiceMenu user={user} />}
		</>
	)
}
